import React, { useMemo } from 'react'
import { CaretLeft, CaretRight, Pause, SidebarSimple } from '@phosphor-icons/react'
import { useDynamicSegment } from 'src/lib/contexts/DynamicSegmentContext'
import { useLocale } from 'src/lib/hooks/useLocal'
import { useRouter } from 'next/router'
import GenericMultiSelectDialog, { MultiSelectItem } from 'src/components/common/select-component/GenericMultiSelectDialog'
import { Button, Icon, IconButton } from '@shopflo/ui'

interface SegmentHeaderProps {
  isRightPanelVisible: boolean
  onToggleRightPanel: () => void
  onBack?: () => void
  compactMode?: boolean
  /** Manager view: navigates to full edit flow */
  onEditSegment?: () => void
  /** Hosted views (e.g. SSO-analytics overlay) show only the segment name, no "Segments >" crumb. */
  hideBreadcrumbPrefix?: boolean
}

const SegmentHeader: React.FC<SegmentHeaderProps> = ({
  isRightPanelVisible,
  onToggleRightPanel,
  onBack,
  compactMode = false,
  onEditSegment,
  hideBreadcrumbPrefix = false
}) => {
  const router = useRouter()
  const { t } = useLocale()
  const { segmentFormState, relationData, actions, isLoadingSegment, isEditMode } = useDynamicSegment()

  const dimensionItems: MultiSelectItem[] = useMemo(() => {
    return relationData.dimensions.map(dimension => ({
      key: dimension.id,
      value: dimension.name
    }))
  }, [relationData.dimensions])

  const selectedDimensionKeys = useMemo(() => {
    return segmentFormState.previewFilters.map(filter => filter.id)
  }, [segmentFormState.previewFilters])

  const handleColumnSelectionChange = (selectedKeys: string[]) => {
    const selectedDimensions = relationData.dimensions.filter(dim => selectedKeys.includes(dim.id))
    actions.setPreviewFilters(selectedDimensions)
  }

  const getColumnsDisplayComponent = useMemo(() => {
    const label =
      selectedDimensionKeys.length === 0
        ? t('segment_creation.columns_label')
        : `${t('segment_creation.columns_label')}: ${selectedDimensionKeys.length}/${relationData.dimensions.length}`
    return (
      <Button variant='ghost' size='sm' leftIcon={Pause}>
        {label}
      </Button>
    )
  }, [selectedDimensionKeys, dimensionItems, relationData.dimensions.length, t])

  const actionButtons = (
    <div className='flex flex-row items-center gap-2'>
      <GenericMultiSelectDialog
        items={dimensionItems}
        selectedKeys={selectedDimensionKeys}
        onSelectionChange={handleColumnSelectionChange}
        placeholderComponent={getColumnsDisplayComponent}
        showSearch={true}
        searchPlaceholder={t('search')}
        showAddButton={true}
        addButtonText={t('done')}
        triggerClassName='!border-0 !px-0'
        contentClassName='w-80'
        align='end'
        requireAtLeastOne={true}
        surface='popover'
      />
      {onEditSegment ? (
        <Button variant='outline' size='sm' onClick={onEditSegment}>
          {t('edit')}
        </Button>
      ) : null}
      <Button
        variant='outline'
        size='sm'
        leftIcon={SidebarSimple}
        onClick={onToggleRightPanel}
        data-active={isRightPanelVisible}
      >
        {t('segment_creation.details_label')}
      </Button>
    </div>
  )

  const handleBack = () => {
    if (onBack) {
      onBack()
      return
    }
    void router.back()
  }

  if (compactMode) {
    return (
      <div className='flex w-full flex-row items-center justify-end px-5 pt-3 pb-1'>
        {actionButtons}
      </div>
    )
  }

  return (
    <header className='z-20 flex w-full shrink-0 items-center justify-between bg-zinc-50 px-12 pt-5 pb-2'>
      <div className='flex items-center gap-2'>
        <IconButton
          variant='outline'
          IconComponent={CaretLeft}
          onClick={handleBack}
          size='sm'
          className='text-zinc-500'
          iconProps={{ weight: 'bold', className: 'w-3 h-3' }}
        />
        <div className='flex items-center text-text-sm-medium'>
          {!hideBreadcrumbPrefix && (
            <>
              <span
                className='cursor-pointer text-text-sm-semibold text-zinc-500 transition-colors hover:text-zinc-700'
                onClick={() => void router.push('/segments')}
              >
                {t('segments')}
              </span>
              <Icon
                IconComponent={CaretRight}
                size='md'
                weight='bold'
                className='text-zinc-600'
                iconProps={{ weight: 'bold', className: 'w-3 h-3' }}
              />
            </>
          )}
          {isEditMode && isLoadingSegment ? (
            <div className='h-5 w-40 min-w-[10rem] animate-pulse rounded-md bg-zinc-200/90' aria-hidden />
          ) : (
            <span className='text-text-sm-semibold text-zinc-600'>
              {Boolean(segmentFormState.name) ? segmentFormState.name : t('untitled')}
            </span>
          )}
        </div>
      </div>
      <div className='flex items-center gap-2'>{actionButtons}</div>
    </header>
  )
}

export default SegmentHeader
